// SAD-482 PUR-02: typed Pursuit team. Exactly one account-backed member is the
// accountable owner; named contributors are recorded by display name and role
// only and get no login, grant or source access.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraCard, SpecraCardContent, SpecraCardHeader, SpecraCardTitle, SpecraEmptyState, SpecraFormField, SpecraInput, SpecraOverlayDialog } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { PlatformUserPicker } from '../Delivery/_shared/components/PlatformUserPicker';
import { RowActionsMenu } from './record/RowActionsMenu';
import { isPermissionDenied, useAddNamedContributor, useCaptureTeam, useRemoveCaptureTeamMember, useSetCaptureTeamOwner } from './teamHooks';
export function CaptureTeamPanel({ captureProjectId, readOnly: readOnlyProp = false }) {
    const team = useCaptureTeam(captureProjectId);
    const addNamed = useAddNamedContributor(captureProjectId);
    const remove = useRemoveCaptureTeamMember(captureProjectId);
    const setOwner = useSetCaptureTeamOwner(captureProjectId);
    const [name, setName] = useState('');
    const [role, setRole] = useState('');
    const [addOpen, setAddOpen] = useState(false);
    const members = team.data ?? [];
    const denied = isPermissionDenied(team.error);
    const ownerMissing = team.isSuccess && !members.some((m)=>m.isOwner);
    const ownerId = members.find((m)=>m.isOwner)?.userId ?? '';
    const mutationError = [
        addNamed,
        remove,
        setOwner
    ].find((m)=>m.error)?.error;
    const error = denied ? undefined : team.error ?? mutationError;
    // GRW-30: read-only comes from the capability gate up front, never from a failed save.
    const readOnly = readOnlyProp || denied;
    const submit = ()=>{
        if (!name.trim()) return;
        addNamed.mutate({
            displayName: name.trim(),
            role: role.trim()
        }, {
            onSuccess: ()=>{
                setName('');
                setRole('');
                setAddOpen(false);
            }
        });
    };
    return /*#__PURE__*/ _jsxs(SpecraCard, {
        "data-testid": Testids.captureTeam.panel,
        children: [
            /*#__PURE__*/ _jsxs(SpecraCardHeader, {
                className: "flex flex-row items-center justify-between space-y-0 py-4",
                children: [
                    /*#__PURE__*/ _jsx(SpecraCardTitle, {
                        children: "Pursuit team"
                    }),
                    !readOnly && members.length > 0 && /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        onClick: ()=>setAddOpen(true),
                        "data-testid": Testids.pursuitTeaming.teamAddButton,
                        children: "Add contributor"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraCardContent, {
                className: "space-y-3",
                children: denied ? /*#__PURE__*/ _jsx("output", {
                    className: "block text-sm text-muted-foreground",
                    children: "You do not have permission to view or change this pursuit's team."
                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        ownerMissing && /*#__PURE__*/ _jsx("p", {
                            role: "alert",
                            className: "text-xs text-destructive",
                            children: "This pursuit has no accountable owner. Pick one account user as owner."
                        }),
                        readOnly && /*#__PURE__*/ _jsx("output", {
                            className: "block text-xs text-muted-foreground",
                            children: "Read-only: you do not have permission to change this team."
                        }),
                        error && /*#__PURE__*/ _jsx("p", {
                            role: "alert",
                            className: "text-xs text-destructive",
                            children: error instanceof Error ? error.message : 'Team request failed.'
                        }),
                        team.isLoading && /*#__PURE__*/ _jsx("p", {
                            className: "text-xs text-muted-foreground",
                            children: "Loading team…"
                        }),
                        /*#__PURE__*/ _jsx("ul", {
                            className: "space-y-1",
                            children: members.map((m)=>/*#__PURE__*/ _jsxs("li", {
                                    "data-testid": Testids.captureTeam.memberRow,
                                    "data-member-kind": m.memberKind,
                                    "data-owner": m.isOwner ? 'true' : 'false',
                                    className: "flex items-center gap-2 rounded border px-2 py-1 text-xs",
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "font-medium",
                                            children: m.displayName
                                        }),
                                        m.role && /*#__PURE__*/ _jsxs("span", {
                                            className: "text-muted-foreground",
                                            children: [
                                                "· ",
                                                m.role
                                            ]
                                        }),
                                        m.isOwner && /*#__PURE__*/ _jsx(SpecraBadge, {
                                            variant: "info",
                                            "data-testid": Testids.captureTeam.ownerBadge,
                                            children: "Owner"
                                        }),
                                        m.memberKind === 'named' && /*#__PURE__*/ _jsx(SpecraBadge, {
                                            variant: "neutral",
                                            "data-testid": Testids.captureTeam.noAccessBadge,
                                            title: "Named for reference only. Naming a contributor does not give them a login or access to sources.",
                                            children: "No access"
                                        }),
                                        !m.isOwner && /*#__PURE__*/ _jsx("span", {
                                            className: "ml-auto",
                                            children: /*#__PURE__*/ _jsx(RowActionsMenu, {
                                                rowName: m.displayName,
                                                readOnly: readOnly,
                                                remove: {
                                                    immediate: true,
                                                    disabled: remove.isPending,
                                                    onConfirm: ()=>remove.mutate(m.id),
                                                    title: '',
                                                    description: ''
                                                }
                                            })
                                        })
                                    ]
                                }, m.id))
                        }),
                        team.isSuccess && members.length === 0 && /*#__PURE__*/ _jsx(SpecraEmptyState, {
                            size: "compact",
                            title: "No team members yet.",
                            description: "Name the contributors working this pursuit.",
                            "data-testid": Testids.pursuitTeaming.teamEmptyState,
                            action: readOnly ? undefined : /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                onClick: ()=>setAddOpen(true),
                                "data-testid": Testids.pursuitTeaming.teamAddButton,
                                children: "Add contributor"
                            })
                        }),
                        !readOnly && /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
                            open: addOpen,
                            onOpenChange: setAddOpen,
                            title: "Add contributor",
                            description: "Named for reference only. Naming a contributor gives them no login or source access.",
                            size: "md",
                            testId: Testids.pursuitTeaming.teamDialog,
                            footerProps: {
                                onCancel: ()=>setAddOpen(false),
                                confirmLabel: 'Add contributor',
                                onConfirm: submit,
                                confirmProps: {
                                    disabled: addNamed.isPending || !name.trim(),
                                    'data-testid': Testids.captureTeam.addNamedSubmit
                                }
                            },
                            children: /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-3",
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                        "aria-label": "Contributor name",
                                        placeholder: "Display name",
                                        value: name,
                                        onChange: (e)=>setName(e.target.value),
                                        "data-testid": Testids.captureTeam.addNamedName
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                        "aria-label": "Contributor role",
                                        placeholder: "Role",
                                        value: role,
                                        onChange: (e)=>setRole(e.target.value),
                                        "data-testid": Testids.captureTeam.addNamedRole
                                    }),
                                    addNamed.error && /*#__PURE__*/ _jsx("p", {
                                        role: "alert",
                                        className: "text-xs text-destructive",
                                        children: addNamed.error instanceof Error ? addNamed.error.message : 'Team request failed.'
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "Accountable owner",
                            htmlFor: "capture-team-owner",
                            children: /*#__PURE__*/ _jsx(PlatformUserPicker, {
                                id: "capture-team-owner",
                                testid: `${Testids.captureTeam.ownerSelect}-search`,
                                triggerTestid: Testids.captureTeam.ownerSelect,
                                value: ownerId,
                                disabled: readOnly || setOwner.isPending,
                                onChange: (userId)=>{
                                    if (userId && userId !== ownerId) setOwner.mutate(userId);
                                }
                            })
                        })
                    ]
                })
            })
        ]
    });
}
